import {
  Button, Card, Image, Input, InputNumber, Modal, Space, Switch, Table, Typography, Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import rentalApi from 'api/rentalApi';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMoney, getRentalErrorMessage } from '../../rentalConstants';

const { Text, Title } = Typography;

const emptyForm = { name: '', vehicleValue: null, imageMediaId: null, imagePreviewUrl: null };

function RentalVehicleModelListScreen() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editModal, setEditModal] = useState(null); // null = closed, {} = create, row = edit
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rentalApi.getVehicleModels();
      setRows(response?.vehicleModels || []);
    } catch (error) {
      setRows([]);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditModal({});
  };

  const openEdit = (row) => {
    setForm({
      name: row.name,
      vehicleValue: Number(row.vehicleValue),
      imageMediaId: row.imageMediaId,
      imagePreviewUrl: row.image?.relativePath ? Utils.getFullUrl(row.image.relativePath) : null,
    });
    setEditModal(row);
  };

  const closeModal = () => {
    setEditModal(null);
    setForm(emptyForm);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const media = await miscHelper.callApiUploadFile(file);
      if (Array.isArray(media) && media.length > 0) {
        setForm((prev) => ({
          ...prev,
          imageMediaId: media[0].mediaId,
          imagePreviewUrl: URL.createObjectURL(file),
        }));
      } else {
        ToastHelper.showError('Tải ảnh thất bại, vui lòng thử lại');
      }
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setUploading(false);
    }
    return false; // prevent antd Upload's own auto-submit
  };

  const submit = async () => {
    if (!form.name.trim() || !form.vehicleValue || form.vehicleValue <= 0) {
      ToastHelper.showError('Vui lòng nhập đủ tên model và giá trị xe hợp lệ');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        vehicleValue: form.vehicleValue,
        imageMediaId: form.imageMediaId || undefined,
      };
      if (editModal?.rentalVehicleModelId) {
        await rentalApi.updateVehicleModel(editModal.rentalVehicleModelId, payload);
        ToastHelper.showSuccess('Đã cập nhật model xe');
      } else {
        await rentalApi.createVehicleModel(payload);
        ToastHelper.showSuccess('Đã thêm model xe vào danh mục');
      }
      closeModal();
      load();
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row, active) => {
    try {
      await rentalApi.updateVehicleModel(row.rentalVehicleModelId, { active });
      ToastHelper.showSuccess(active ? 'Đã bật lại model này' : 'Đã ngừng cung cấp model này');
      load();
    } catch (error) {
      ToastHelper.showError(getRentalErrorMessage(error));
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Ảnh',
      key: 'image',
      width: 90,
      render: (_, row) => (row.image?.relativePath ? (
        <Image src={Utils.getFullUrl(row.image.relativePath)} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 8 }} />
      ) : <div style={{ width: 56, height: 56, background: '#f0f0f0', borderRadius: 8 }} />),
    },
    { title: 'Tên model', dataIndex: 'name', render: (value) => <Text strong>{value}</Text> },
    { title: 'Giá trị xe tham chiếu', dataIndex: 'vehicleValue', render: formatMoney },
    {
      title: 'Đang cung cấp',
      key: 'active',
      width: 140,
      render: (_, row) => (
        <Switch checked={row.active} onChange={(checked) => toggleActive(row, checked)} />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_, row) => <Button size="small" onClick={() => openEdit(row)}>Sửa</Button>,
    },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Danh mục xe cho thuê</Title>
            <Text type="secondary">Đối tác chỉ được chọn xe từ danh mục này khi thêm xe, không tự gõ model/giá trị xe nữa</Text>
          </div>
          <Space>
            <Button onClick={load} loading={loading}>Làm mới</Button>
            <Button type="primary" onClick={openCreate}>+ Thêm model</Button>
          </Space>
        </div>
        <Table
          rowKey="rentalVehicleModelId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          locale={{ emptyText: 'Chưa có model xe nào trong danh mục' }}
          pagination={false}
        />
      </Card>

      <Modal
        title={editModal?.rentalVehicleModelId ? `Sửa model #${editModal.rentalVehicleModelId}` : 'Thêm model xe'}
        open={Boolean(editModal)}
        okText={editModal?.rentalVehicleModelId ? 'Cập nhật' : 'Thêm'}
        cancelText="Huỷ"
        confirmLoading={saving}
        onCancel={closeModal}
        onOk={submit}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text>Tên model</Text>
            <Input
              placeholder="Ví dụ: VinFast Evo200"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
          </div>
          <div>
            <Text>Giá trị xe tham chiếu (VNĐ)</Text>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              placeholder="Ví dụ: 25000000"
              value={form.vehicleValue}
              onChange={(value) => setForm((prev) => ({ ...prev, vehicleValue: value }))}
            />
          </div>
          <div>
            <Text>Ảnh minh hoạ</Text>
            <div className="d-flex align-items-center gap-3">
              {form.imagePreviewUrl && (
                <Image src={form.imagePreviewUrl} width={64} height={64} style={{ objectFit: 'cover', borderRadius: 8 }} />
              )}
              <Upload accept="image/png,image/jpg,image/jpeg" showUploadList={false} beforeUpload={handleUpload}>
                <Button icon={<UploadOutlined />} loading={uploading}>Chọn ảnh</Button>
              </Upload>
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
}

export default RentalVehicleModelListScreen;
